import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminProductsApi } from '../api';
import { useTelegram } from '../hooks/useTelegram';
import { Loading } from '../components/Loading';
import type { Product } from '../types';
import './AdminProductsPage.css';

interface ProductsByCategory {
  [categoryName: string]: Product[];
}

export const AdminProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const { tg } = useTelegram();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<number | null>(null);
  const [reorderingProductId, setReorderingProductId] = useState<number | null>(null);

   
  useEffect(() => {
    // Show back button
    if (tg?.BackButton) {
      tg.BackButton.show();
      tg.BackButton.onClick(handleBack);
    }

    return () => {
      if (tg?.BackButton) {
        tg.BackButton.hide();
        tg.BackButton.offClick(handleBack);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg]);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminProductsApi.getAdminCatalog();

      // Extract all products from all categories
      const allProducts: Product[] = [];
      response.categories.forEach((category) => {
        if (category.products && category.products.length > 0) {
          allProducts.push(...category.products);
        }
      });

      setProducts(allProducts);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить товары');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin');
  };

  const handleAddProduct = () => {
    navigate('/admin/products/create');
  };

  const handleProductClick = (productId: number) => {
    navigate(`/admin/products/${productId}/edit`);
  };

  const handleReorder = async (e: React.MouseEvent, product: Product, direction: 'up' | 'down') => {
    e.stopPropagation(); // Prevent opening product edit page

    try {
      setReorderingProductId(product.id);

      // Call API to reorder
      const response = await adminProductsApi.reorderProduct(product.id, direction);

      // Update products list - replace products from this category with updated ones
      setProducts((prev) => {
        // Remove old products from this category
        const otherProducts = prev.filter((p) => p.category_id !== product.category_id);
        // Add updated products
        return [...otherProducts, ...response];
      });

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.impactOccurred('light');
      }
    } catch (err: unknown) {
      console.error('Failed to reorder product:', err);
      tg?.showAlert(err instanceof Error ? err.message : 'Не удалось изменить порядок товара');

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setReorderingProductId(null);
    }
  };

  const handleDeleteProduct = async (e: React.MouseEvent, product: Product) => {
    e.stopPropagation(); // Prevent opening product edit page

    tg?.showConfirm(
      `Вы уверены, что хотите удалить товар "${product.name}"?`,
      async (confirmed) => {
        if (!confirmed) return;

        try {
          setDeletingProductId(product.id);
          await adminProductsApi.deleteProduct(product.id);

          // Remove product from list
          setProducts((prev) => prev.filter((p) => p.id !== product.id));

          tg?.showAlert('Товар успешно удален');

          if (tg?.HapticFeedback) {
            tg.HapticFeedback.notificationOccurred('success');
          }
        } catch (err: unknown) {
          tg?.showAlert(err instanceof Error ? err.message : 'Ошибка удаления товара');

          if (tg?.HapticFeedback) {
            tg.HapticFeedback.notificationOccurred('error');
          }
        } finally {
          setDeletingProductId(null);
        }
      }
    );
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} ₽`;
  };

  // Group products by category
  const productsByCategory: ProductsByCategory = products.reduce((acc, product) => {
    const categoryName = product.category_name || 'Без категории';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(product);
    return acc;
  }, {} as ProductsByCategory);

  // Sort products within each category by sort_order
  Object.keys(productsByCategory).forEach((categoryName) => {
    productsByCategory[categoryName].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  });

  const categoryNames = Object.keys(productsByCategory).sort();

  if (loading) {
    return (
      <div className="page admin-products-page">
        <div className="admin-products-header">
          <h1 className="admin-products-title">Управление товарами</h1>
        </div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page admin-products-page">
        <div className="admin-products-header">
          <h1 className="admin-products-title">Управление товарами</h1>
        </div>
        <div className="admin-products-error">
          <p className="admin-products-error-message">{error}</p>
          <button className="admin-products-retry-btn" onClick={loadProducts}>
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page admin-products-page">
      <div className="admin-products-header">
        <h1 className="admin-products-title">Управление товарами</h1>
        <button className="admin-products-add-btn" onClick={handleAddProduct}>
          + Добавить товар
        </button>
      </div>

      <div className="admin-products-content">
        {categoryNames.length === 0 ? (
          <div className="admin-products-empty">
            <p className="admin-products-empty-message">Товары не найдены</p>
          </div>
        ) : (
          categoryNames.map((categoryName) => (
            <div key={categoryName} className="admin-products-category">
              <h2 className="admin-products-category-title">{categoryName}</h2>
              <div className="admin-products-list">
                {productsByCategory[categoryName].map((product, index) => (
                  <div
                    key={product.id}
                    className="admin-products-item"
                    onClick={() => handleProductClick(product.id)}
                  >
                    <div className="admin-products-item-image">
                      <img
                        src={product.image_url || 'https://via.placeholder.com/60x60?text=No+Image'}
                        alt={product.name}
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/60x60?text=No+Image';
                        }}
                      />
                    </div>

                    <div className="admin-products-item-info">
                      <h3 className="admin-products-item-name">{product.name}</h3>
                      <div className="admin-products-item-details">
                        <span className="admin-products-item-price">{formatPrice(product.price)}</span>
                        <span className="admin-products-item-divider">•</span>
                        <span className={`admin-products-item-status ${product.is_available ? 'available' : 'unavailable'}`}>
                          {product.is_available ? 'В наличии' : 'Недоступен'}
                        </span>
                      </div>
                    </div>

                    <div className="admin-products-item-actions">
                      <div className="admin-products-reorder-buttons">
                        <button
                          className="admin-products-reorder-btn"
                          onClick={(e) => handleReorder(e, product, 'up')}
                          disabled={index === 0 || reorderingProductId === product.id}
                          title="Переместить вверх"
                        >
                          {reorderingProductId === product.id ? (
                            <div className="admin-products-spinner-small"></div>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M8 4L3 9L4.5 10.5L8 7L11.5 10.5L13 9L8 4Z"
                                fill="currentColor"
                              />
                            </svg>
                          )}
                        </button>
                        <button
                          className="admin-products-reorder-btn"
                          onClick={(e) => handleReorder(e, product, 'down')}
                          disabled={index === productsByCategory[categoryName].length - 1 || reorderingProductId === product.id}
                          title="Переместить вниз"
                        >
                          {reorderingProductId === product.id ? (
                            <div className="admin-products-spinner-small"></div>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M8 12L13 7L11.5 5.5L8 9L4.5 5.5L3 7L8 12Z"
                                fill="currentColor"
                              />
                            </svg>
                          )}
                        </button>
                      </div>

                      <button
                        className="admin-products-delete-btn"
                        onClick={(e) => handleDeleteProduct(e, product)}
                        disabled={deletingProductId === product.id}
                      >
                        {deletingProductId === product.id ? (
                          <div className="admin-products-spinner"></div>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M4 5H16M8 8V14M12 8V14M5 5L6 16C6 16.5304 6.21071 17.0391 6.58579 17.4142C6.96086 17.7893 7.46957 18 8 18H12C12.5304 18 13.0391 17.7893 13.4142 17.4142C13.7893 17.0391 14 16.5304 14 16L15 5M7 5V3C7 2.73478 7.10536 2.48043 7.29289 2.29289C7.48043 2.10536 7.73478 2 8 2H12C12.2652 2 12.5196 2.10536 12.7071 2.29289C12.8946 2.48043 13 2.73478 13 3V5"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
